import { worksheetCache } from "../../workbook/cache";
import { ColumnNameToIndexMap, getReportStartRange } from "./common";

export type PreservedContext = {
  values: PreservedValuesByColumn;
  keyColumnIndices: number[];
};

type PreservedValuesByColumn = Record<string, Record<string, string | number | null>>;

const PRESERVE_COLUMN_PREFIX = "save_";
const PRESERVED_VALUES_BLANK_KEY = "\0";

const getPreserveColumnNamesByPrefix = (columnMap: ColumnNameToIndexMap): string[] =>
  Object.keys(columnMap).filter(
    (name) => typeof columnMap[name] === "number" && name.startsWith(PRESERVE_COLUMN_PREFIX)
  );

export const getPreservedKeyColumnIndices = (columnMap: ColumnNameToIndexMap, groupBy?: string | null): number[] => {
  const preservedKeyNameColumns = ["name", "description", "label"];
  const codeOffset = typeof columnMap.code === "number" ? columnMap.code : undefined;
  const nameOffset = preservedKeyNameColumns.map((k) => columnMap[k]).find((v) => typeof v === "number");

  if (groupBy === "floor_group" || groupBy === "use_group" || groupBy === "markups") {
    return nameOffset !== undefined ? [nameOffset] : [1];
  }
  if (groupBy === "contracting") {
    if (codeOffset !== undefined && nameOffset !== undefined) return [codeOffset, nameOffset];
    return [0, 1];
  }
  return codeOffset !== undefined ? [codeOffset] : [0];
};

const getNumericColumnEntries = (columnMap: ColumnNameToIndexMap): { name: string; offset: number }[] => {
  // if there are no numeric columns, return an empty array
  if (Object.keys(columnMap).length === 0) return [];
  // get the numeric column entries
  const numericColumnEntries = Object.keys(columnMap)
    .filter((k): k is string => k !== "startColumnIndex" && typeof columnMap[k] === "number")
    .map((k) => ({ name: k, offset: columnMap[k] as number }));

  // if there are no numeric column entries, return an empty array
  if (numericColumnEntries.length === 0) return [];
  // return the numeric column entries
  return numericColumnEntries;
};

const buildPreservedRowKey = (parts: (string | number | null | undefined)[]): string => {
  const PRESERVED_VALUES_COMPOSITE_KEY_DELIMITER = "\u001E";
  const rawKey = parts.map((p) => String(p ?? "").trim()).join(PRESERVED_VALUES_COMPOSITE_KEY_DELIMITER);
  if (rawKey == null || String(rawKey).trim() === "") {
    return PRESERVED_VALUES_BLANK_KEY;
  }
  return String(rawKey).trim();
};

export const shouldUsePreservedValues = async (
  worksheet: Excel.Worksheet,
  columnMap: ColumnNameToIndexMap,
  currentId: string,
  newGroupBy?: string | null
): Promise<boolean> => {
  const hasPreserveColumns = getPreserveColumnNamesByPrefix(columnMap).length > 0;
  if (!hasPreserveColumns) return false;

  const cached = await worksheetCache.getOptions(worksheet);
  if (cached?.parentId === undefined && cached?.groupBy === undefined) return true;
  return cached?.parentId === currentId && cached?.groupBy === newGroupBy;
};

export const applyPreservedValuesToRow = (
  row: (string | number | null)[],
  columnMap: ColumnNameToIndexMap,
  preserved: PreservedContext
): void => {
  const { values: preservedValues, keyColumnIndices } = preserved;
  const parts = keyColumnIndices.map((i) => row[i] ?? "");
  const rowKey = buildPreservedRowKey(parts);
  const fallbackKey = rowKey === PRESERVED_VALUES_BLANK_KEY ? "blank" : null;
  for (const col of Object.keys(preservedValues)) {
    const index = columnMap[col];
    if (index === undefined) continue;
    const colValues = preservedValues[col];
    row[index] = colValues[rowKey] ?? (fallbackKey != null ? colValues[fallbackKey] : null) ?? null;
  }
};

export const getPreservedColumnValues = async (
  worksheet: Excel.Worksheet,
  insertAtRangeName: string,
  columnMap: ColumnNameToIndexMap,
  keyColumnIndices: number[] = [0]
): Promise<PreservedValuesByColumn> => {
  const EXCEL_MAX_COLUMNS = 16384;
  const preserveNames = getPreserveColumnNamesByPrefix(columnMap);
  if (preserveNames.length === 0) return {};

  const range = await getReportStartRange(worksheet, insertAtRangeName);
  if (!range || range.columnIndex < 0 || range.columnIndex >= EXCEL_MAX_COLUMNS) return {};

  const entries = getNumericColumnEntries(columnMap);
  const preserveOffsets = entries.filter((e) => e.name.startsWith(PRESERVE_COLUMN_PREFIX));
  if (preserveOffsets.length === 0) return {};

  const result: PreservedValuesByColumn = {};
  for (const { name } of preserveOffsets) result[name] = {};

  const rangeColumnDelta = range.columnIndex - columnMap.startColumnIndex;
  const valueAt = (row: (string | number | null)[], offset: number): string | number | null => {
    const i = offset - rangeColumnDelta;
    return i >= 0 && i < row.length ? (row[i] ?? null) : null;
  };

  const keyOffsets = keyColumnIndices.length ? keyColumnIndices : [0];
  const maxOffset = Math.max(-1, ...entries.map((e) => e.offset), ...keyOffsets);
  const maxSafeColumns = EXCEL_MAX_COLUMNS - range.columnIndex;
  const minColumnsNeeded = maxOffset - rangeColumnDelta + 1;
  const columnCount = Math.min(Math.max(range.columnCount, minColumnsNeeded), maxSafeColumns);
  const readRange = columnCount > range.columnCount ? range.getResizedRange(0, columnCount - range.columnCount) : range;
  readRange.load("values");
  await worksheet.context.sync();

  const rows = readRange.values as (string | number | null)[][];
  if (!rows?.length) return result;

  for (const row of rows) {
    const parts = keyOffsets.map((off) => {
      const v = valueAt(row, off);
      return v != null ? String(v).trim() : "";
    });
    const rowKey = buildPreservedRowKey(parts);
    for (const { name: col, offset } of preserveOffsets) {
      result[col][rowKey] = valueAt(row, offset);
    }
  }
  return result;
};
